import { BaseService } from "../BaseService";
import {
  CreateUpdateWorkflow,
  CreateUpdateWorkflowStep,
  TriggerWorkflowRequest,
  Workflow,
  WorkflowStep,
} from "@/types/breeze/Workflow";
import { WorkflowJob } from "@/types/breeze/WorkflowJob";
import { PaginatedResult } from "@/types/breeze/Any";

export class WorkflowService extends BaseService {
  public async getAllWorkflows(): Promise<Workflow[]> {
    return this.getRequest("/workflows");
  }

  public async getWorkflow(workflowId: number): Promise<Workflow> {
    return this.getRequest(`/workflows/${workflowId}`);
  }

  public async getWorkflowJobHistory(
    workflowId: number,
    page: number = 1,
    pageSize: number = 25,
  ): Promise<PaginatedResult<WorkflowJob>> {
    return this.getRequest(
      `/workflows/${workflowId}/jobs?page=${page}&pageSize=${pageSize}`,
    );
  }

  public async getWorkflowJobHistoryById(
    workflowId: number,
    jobId: number,
  ): Promise<WorkflowJob> {
    return this.getRequest(`/workflows/${workflowId}/jobs/${jobId}`);
  }

  public async createWorkflow(
    workflow: CreateUpdateWorkflow,
  ): Promise<Workflow> {
    return this.postRequest("/workflows", workflow, "Workflow created");
  }

  public async updateWorkflow(
    workflowId: number,
    workflow: CreateUpdateWorkflow,
  ): Promise<Workflow> {
    return this.putRequest(
      `/workflows/${workflowId}`,
      workflow,
      "",
      true,
      true,
    );
  }

  public async createWorkflowStep(
    workflowId: number,
    workflowStep: CreateUpdateWorkflowStep,
  ): Promise<WorkflowStep> {
    return this.postRequest(
      `/workflows/${workflowId}/steps`,
      workflowStep,
      "Workflow step created",
    );
  }

  public async updateWorkflowStep(
    workflowId: number,
    workflowStepId: number,
    workflowStep: CreateUpdateWorkflowStep,
  ): Promise<WorkflowStep> {
    return this.putRequest(
      `/workflows/${workflowId}/steps/${workflowStepId}`,
      workflowStep,
      "",
      true,
      true,
    );
  }

  public async enableWorkflow(workflowId: number): Promise<void> {
    return this.postRequest(
      `/workflows/${workflowId}/enable`,
      {},
      "Workflow enabled",
    );
  }

  public async disableWorkflow(workflowId: number): Promise<void> {
    return this.postRequest(
      `/workflows/${workflowId}/disable`,
      {},
      "Workflow disabled",
    );
  }

  public async triggerWorkflow(
    workflowId: number,
    data: TriggerWorkflowRequest,
  ): Promise<void> {
    return this.postRequest(
      `/workflows/${workflowId}/trigger`,
      data,
      "Workflow triggered successfully",
    );
  }

  public async deleteWorkflow(workflowId: number): Promise<void> {
    return this.deleteRequest(`/workflows/${workflowId}`, "Workflow deleted");
  }

  public async deleteWorkflowStep(
    workflowId: number,
    workflowStepId: number,
  ): Promise<void> {
    return this.deleteRequest(
      `/workflows/${workflowId}/steps/${workflowStepId}`,
      "Workflow step deleted",
    );
  }
}

export const workflowService = new WorkflowService();
